<template>
  <div class="card activity-card" :class="activityClass">
    <div class="card-body">
      <div class="card-title">
        <h5>
          <router-link
            v-slot="{ href, route, navigate }"
            :to="{
              name: 'activity/RotaActivityView',
              params: { rotaActivityId: activity.id },
            }"
            custom
          >
            <a
              :href="href"
              @click="onTitleClick(route, $event)"
              @keypress.enter="navigate"
            >
              <font-awesome-icon
                v-if="getFontAwesomeIconForActivity(activity)"
                :icon="getFontAwesomeIconForActivity(activity)"
                class="me-1"
              />
              <span v-if="startDay" class="activity-day me-1">
                {{ startDay }}
              </span>
              <span class="activity-time me-1">
                {{ time(activity) }}
              </span>
              <span class="activity-title" data-dd-privacy="mask">
                {{ activity.title }}
              </span>
            </a>
          </router-link>
        </h5>
        <span v-if="showAsStarted(activity)">
          <font-awesome-icon fixed-width icon="play" />
          Started
          {{
            formatTimeHoursMins(
              activity.start?.date ?? activity.actualStartDate,
            )
          }}
        </span>
        <span v-if="showAsStopped(activity)">
          <font-awesome-icon fixed-width icon="check" />
          Finished
          {{
            formatTimeHoursMins(activity.stop?.date ?? activity.actualEndDate)
          }}
        </span>

        <client-badge-with-activity-status
          v-for="client in activity.clients"
          :key="client.id"
          :client="client"
          :show-location="false"
          :show-room-number="true"
          class="d-flex align-items-baseline justify-content-between"
        />
      </div>
      <div v-if="showShifts && activity.shifts">
        <template v-for="shift in activity.shifts" :key="shift.shiftId">
          <span v-if="shouldHideShift(shift) === false">
            <user-display-resolver
              :user-id="shift.userId"
              :agency-id="shift.agencyId"
              :display-name="shift.displayName"
              :scale="0.5"
              class="d-inline me-2"
            />
          </span>
        </template>
      </div>
      <p
        v-if="activity.note?.length > 0"
        class="card-text note"
        data-dd-privacy="mask"
      >
        {{ activity.note }}
      </p>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { isBefore, startOfDay, format } from "date-fns";
import { useAuthStore } from "@switchsystems/coactive-vue-lib";
import { formatTimeHoursMins } from "../../lib/date-functions";
import { getActionIconIDText } from "../../lib/constants/pcs-functions";
import {
  getCSSClassesFromActivity,
  getFontAwesomeIconForActivity,
} from "../../lib/rota/activity-functions";
import ClientBadgeWithActivityStatus from "../ui/ClientBadgeWithActivityStatus.vue";
import UserDisplayResolver from "@/components/UserDisplayResolver.vue";
import usePopupActivity from "./usePopupActivity";

export default {
  components: {
    ClientBadgeWithActivityStatus,
    UserDisplayResolver,
  },
  props: {
    activity: {
      type: Object,
      required: true,
    },
    linkStyle: {
      type: String,
      default: "popup", // or router-link
    },
    showShifts: {
      type: Boolean,
      default: () => true,
    },
    hideSelfShifts: {
      type: Boolean,
      default: () => false,
    },
    showEndTime: {
      type: Boolean,
      default: () => false,
    },
  },
  setup(props) {
    const router = useRouter();
    const authStore = useAuthStore();

    function shouldHideShift(shift) {
      if (props.hideSelfShifts === false) {
        return false;
      }
      if (authStore.isAgencyUser) {
        return shift.agencyId === authStore.user.agencyId;
      }
      return shift.userId === authStore.user.id;
    }

    const { showViewActivityPopup } = usePopupActivity();
    function onTitleClick(linkRoute, event) {
      event.preventDefault();
      event.stopPropagation();
      if (props.linkStyle !== "popup") {
        router.push(linkRoute);
        return;
      }
      showViewActivityPopup({ rotaActivity: props.activity });
    }

    const showAsStarted = (activity) =>
      (activity?.start || activity?.actualStartDate) &&
      (!activity?.stop || !activity?.actualEndDate);
    const showAsStopped = (activity) =>
      activity?.stop || activity?.actualEndDate;

    const activityClass = computed(() =>
      getCSSClassesFromActivity(props.activity),
    );

    return {
      showAsStarted,
      showAsStopped,
      formatTimeHoursMins,
      getFontAwesomeIconForActivity,
      getActionIconIDText,
      onTitleClick,
      shouldHideShift,
      time: (activity) => {
        let str = formatTimeHoursMins(activity.startDate);
        if (props.showEndTime) {
          str += ` - ${formatTimeHoursMins(activity.endDate)}`;
        }
        return str;
      },
      activityClass,
    };
  },
  computed: {
    startDay() {
      const { startDate } = this.activity;
      const today = new Date();
      return isBefore(startDate, startOfDay(today))
        ? format(startDate, "E")
        : "";
    },
  },
};
</script>
